import React, { useState, useEffect } from 'react';
import { useJazyk } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LotusLogo, getAvatarImage } from '../components/Layout';
import { useContactEmail } from '../hooks/useContactEmail';
import { useAppSettings, DEFAULT_SECTIONS } from '../context/AppSettingsContext';
import { nazevSekce } from '../lib/nazvySekci';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { 
  Heart, Stethoscope, Scale, Newspaper, ArrowRight, MapPin, Calendar,
  Users, Star, MessageSquare
} from 'lucide-react';

import { API, mediaUrl } from '../lib/api';
import { stripHtmlPreview } from '../lib/utils';

// Názvy sekcí u otázek. Berou se ze slovníku, ať jsou v jazyce rozhraní.
const SECTION_KEYS = { legal: 'nav.legal', specialists: 'nav.specialists' };
const SECTION_PATHS = { legal: '/legal', specialists: '/specialists' };
const SECTION_COLORS = { legal: 'bg-blue-100 text-blue-700', specialists: 'bg-teal-100 text-teal-700' };

const HomePage = () => {
  const { t } = useJazyk();
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [highlights, setHighlights] = useState({ new_members: [], top_specialists: [] });
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [stats, setStats] = useState(null);
  const contactEmail = useContactEmail();
  const { sections, markerColors } = useAppSettings();

  useEffect(() => { fetchNews(); fetchHighlights(); fetchFeatured(); fetchStats(); fetchRecentQuestions(); }, []);

  const fetchNews = async () => {
    try { const r = await axios.get(`${API}/news`); setNews(r.data.slice(0, 6)); } catch {}
  };
  const fetchHighlights = async () => {
    try { const r = await axios.get(`${API}/community/highlights`); setHighlights(r.data); } catch {}
  };
  const fetchFeatured = async () => {
    try { const r = await axios.get(`${API}/featured-items`); setFeaturedItems(r.data); } catch {}
  };
  const fetchStats = async () => {
    try { const r = await axios.get(`${API}/stats`); setStats(r.data); } catch {}
  };
  const fetchRecentQuestions = async () => {
    try {
      const [legal, specialists] = await Promise.all([
        axios.get(`${API}/questions?section=legal`),
        axios.get(`${API}/questions?section=specialists`),
      ]);
      const combined = [
        ...legal.data.map(q => ({ ...q, section: 'legal' })),
        ...specialists.data.map(q => ({ ...q, section: 'specialists' })),
      ];
      combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecentQuestions(combined.slice(0, 5));
    } catch {}
  };

  const mc = (key) => markerColors?.[key] || '#8A7CFF';

  // Feature cards — titles and descriptions from admin settings (with fallback), filtered by visibility
  const BASE_FEATURES = [
    { icon: Heart, klicNazvu: 'nav.support', klicPopisu: 'domu.dlazdice.support', link: '/support', color: 'text-bloom-pride-pink bg-bloom-pride-pink/10', sectionKey: 'support' },
    { icon: Stethoscope, klicNazvu: 'nav.specialists', klicPopisu: 'domu.dlazdice.specialists', link: '/specialists', color: 'text-bloom-pride-blue bg-bloom-pride-blue/10', sectionKey: 'specialists' },
    { icon: Scale, klicNazvu: 'nav.legal', klicPopisu: 'domu.dlazdice.legal', link: '/legal', color: 'text-bloom-violet bg-bloom-violet/10', sectionKey: 'legal' },
    { icon: MapPin, klicNazvu: 'nav.nearby', klicPopisu: 'domu.dlazdice.nearby', link: '/nearby', color: 'text-bloom-mint bg-bloom-mint/10', sectionKey: 'nearby' },
  ];
  // Název i popis se překládají, dokud je správkyně nepřejmenovala – stejné
  // pravidlo jako v liště a v nadpisech sekcí.
  const features = BASE_FEATURES
    .filter(f => sections?.[f.sectionKey]?.visible !== false)
    .map(f => ({
      ...f,
      title: nazevSekce(t, sections?.[f.sectionKey]?.title, f.klicNazvu, DEFAULT_SECTIONS[f.sectionKey]?.title),
      desc: nazevSekce(t, sections?.[f.sectionKey]?.subtitle, f.klicPopisu, DEFAULT_SECTIONS[f.sectionKey]?.subtitle),
    }));

  const featuredSpecialists = featuredItems.filter(f => f.type === 'specialist');
  const featuredNews = featuredItems.filter(f => f.type === 'news');

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero — compact */}
      <section className="relative pt-0 pb-3 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-2 animate-fade-in">
            <div className="p-2 rounded-xl bg-white shadow-md shadow-bloom-violet/10">
              {user?.avatar ? (
                <Avatar className="w-10 h-10 sm:w-12 sm:h-12">
                  <AvatarImage src={getAvatarImage(user.avatar)} alt={user.username} />
                  <AvatarFallback className="bg-bloom-violet/10"><LotusLogo size={28} /></AvatarFallback>
                </Avatar>
              ) : (
                <LotusLogo size={36} />
              )}
            </div>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-1 text-bloom-text/75 animate-fade-in" data-testid="hero-title">
            {t('domu.vitej')} <span className="text-bloom-violet">{user?.username}</span>
          </h1>
        </div>
      </section>

      {/* Pride bar */}
      <div className="pride-bar" />

      {/* Quote + community stats */}
      <section className="py-5 sm:py-7">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          {/* Quote */}
          <blockquote className="relative">
            <div className="absolute -top-2 left-0 text-5xl text-bloom-violet/20 font-serif leading-none select-none">&bdquo;</div>
            <p className="text-base sm:text-lg text-bloom-sub leading-relaxed italic px-6">
              {t('domu.citat')}
            </p>
            <footer className="mt-3 text-sm font-medium text-bloom-violet">
              {t('domu.citatAutor')}
              <span className="font-normal opacity-70">{t('domu.prelozenoZCestiny')}</span>
            </footer>
          </blockquote>

          {/* Bloom v číslech */}
          {stats && (
            <div>
              <p className="text-xs font-semibold text-bloom-sub uppercase tracking-widest mb-4">{t('domu.vCislech')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { emoji: '🌸', value: stats.members, label: t('domu.pocetClenu', { pocet: stats.members }) },
                  { emoji: '💬', value: stats.experiences, label: t('domu.pocetZkusenosti', { pocet: stats.experiences }) },
                  { emoji: '🧠', value: stats.specialists, label: t('domu.pocetOdborniku', { pocet: stats.specialists }) },
                  { emoji: '🤝', value: stats.services, label: t('domu.pocetNabidek', { pocet: stats.services }) },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-border/50 rounded-xl py-3 px-2 shadow-sm" data-testid={`stat-${s.label.split(' ')[0]}`}>
                    <div className="text-xl mb-1">{s.emoji}</div>
                    <div className="text-2xl font-bold text-bloom-text">{s.value}</div>
                    <div className="text-[11px] text-bloom-sub leading-tight mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== SAFE SPACE ONBOARDING ===== */}
      <section className="py-8 sm:py-10 bg-gradient-to-b from-bloom-violet/5 to-transparent" data-testid="onboarding-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <p className="text-sm text-bloom-sub/70 tracking-wide uppercase font-medium mb-2">{t('domu.noveVKomunite')}</p>
            <p className="text-sm text-bloom-sub max-w-lg mx-auto leading-relaxed">
              {t('domu.noveVKomunitePopis1')}<br className="hidden sm:block" />
              {t('domu.noveVKomunitePopis2')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mb-4">
            {[
              { label: t('domu.rozcestnik1'), link: '/news', color: '#F5A9B8' },
              { label: t('domu.rozcestnik2'), link: '/legal', color: '#8A7CFF' },
              { label: t('domu.rozcestnik3'), link: '/profile', color: '#5BCEFA' },
              { label: t('domu.rozcestnik4'), link: '/specialists', color: '#A8E6CF' },
              { label: t('domu.rozcestnik5'), link: '/news', color: '#F7C59F' },
            ].map(entry => (
              <Link
                key={entry.label}
                to={entry.link}
                className="px-4 py-2 rounded-full text-sm font-medium border transition-all hover:shadow-sm hover:scale-[1.02]"
                style={{ borderColor: `${entry.color}80`, color: '#2F3441', background: `${entry.color}22` }}
                data-testid={`onboarding-entry-${entry.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {entry.label}
              </Link>
            ))}
          </div>
          <div className="text-center">
            <p className="text-xs text-bloom-sub/60 max-w-md mx-auto leading-relaxed">
              {t('domu.otazky')}{' '}
              {contactEmail && <a href={`mailto:${contactEmail}`} className="text-bloom-violet hover:underline">{contactEmail}</a>}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Link key={f.title} to={f.link} data-testid={`feature-${f.link.replace('/', '')}`}>
                  <Card className="h-full bg-white border-border/50 card-hover group">
                    <CardContent className="p-5">
                      <div className={`w-10 h-10 rounded-lg ${f.color} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
                      <h3 className="font-serif text-lg font-semibold text-bloom-text mb-1 group-hover:text-bloom-violet transition-colors">{f.title}</h3>
                      <p className="text-sm text-bloom-sub">{f.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Items — only shown if admin has pinned items */}
      {featuredItems.length > 0 && (
        <section className="py-10 bg-gradient-to-b from-bloom-pride-pink/5 to-transparent" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-bloom-text mb-6 flex items-center">
              <span className="section-marker" style={{ background: mc('featured') }} />
              {t('domu.doporucujeme')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Featured specialists */}
              {featuredSpecialists.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-bloom-sub uppercase tracking-wider mb-3">{t('domu.odbornici')}</h3>
                  <div className="space-y-3">
                    {featuredSpecialists.map(f => (
                      <Link key={f.data.id} to="/specialists">
                        <Card className="bg-white border-border/50 card-hover" data-testid={`featured-spec-home-${f.data.id}`}>
                          <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-bloom-pride-pink/10 flex items-center justify-center shrink-0"><Stethoscope className="w-5 h-5 text-bloom-pride-pink" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-bloom-text text-sm">{f.data.name}</p>
                              <p className="text-xs text-bloom-violet">{f.data.specialty}</p>
                              <p className="text-xs text-bloom-sub flex items-center gap-1"><MapPin className="w-3 h-3" />{f.data.city}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Star className="w-3.5 h-3.5 text-bloom-pride-pink fill-bloom-pride-pink" />
                              <span className="text-sm font-medium text-bloom-text">{f.data.avg_rating?.toFixed(1)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {/* Featured news */}
              {featuredNews.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-bloom-sub uppercase tracking-wider mb-3">{t('nav.news')}</h3>
                  <div className="space-y-3">
                    {featuredNews.map(f => (
                      <Link key={f.data.id} to={`/news/${f.data.id}`}>
                        <Card className="bg-white border-border/50 card-hover" data-testid={`featured-news-home-${f.data.id}`}>
                          <CardContent className="p-4 flex gap-3 items-start">
                            {f.data.image_url && (
                              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0"><img src={mediaUrl(f.data.image_url)} alt={f.data.title} className="w-full h-full object-cover" /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-bloom-text text-sm line-clamp-2">{f.data.title}</p>
                              <p className="text-xs text-bloom-sub mt-0.5 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(f.data.created_at).toLocaleDateString('cs-CZ')}</p>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Community Highlights */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-bloom-text mb-8 flex items-center"><span className="section-marker" style={{background: mc('community')}} />{nazevSekce(t, sections?.community?.title, 'kom.titulek', DEFAULT_SECTIONS.community?.title)}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* New Members */}
            <Card className="bg-white border-border/50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-bloom-violet" />
                  <h3 className="font-semibold text-bloom-text">{t('domu.noviClenove')}</h3>
                </div>
                {highlights.new_members.length === 0 ? (
                  <p className="text-sm text-bloom-sub">{t('domu.zadniNovi')}</p>
                ) : (
                  <div className="space-y-3">
                    {highlights.new_members.map((m) => (
                      <Link key={m.id} to={`/users/${m.id}`} className="flex items-center gap-3 hover:bg-muted/50 rounded-lg p-1 -m-1 transition-colors" data-testid={`member-link-${m.id}`}>
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={getAvatarImage(m.avatar)} />
                          <AvatarFallback className="bg-bloom-violet text-white text-xs">{m.username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-bloom-text hover:text-bloom-violet transition-colors">{m.username}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Recent Questions */}
            <Card className="bg-white border-border/50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-bloom-violet" />
                  <h3 className="font-semibold text-bloom-text">{t('domu.nejnovejsiOtazky')}</h3>
                </div>
                {recentQuestions.length === 0 ? (
                  <p className="text-sm text-bloom-sub">{t('domu.zadneOtazky')}</p>
                ) : (
                  <div className="space-y-2.5">
                    {recentQuestions.map(q => (
                      <Link key={q.id} to={SECTION_PATHS[q.section]} className="block group" data-testid={`recent-question-${q.id}`}>
                        <div className="flex flex-col gap-1 rounded-lg p-2 -mx-2 hover:bg-muted/50 transition-colors">
                          <p className="text-sm text-bloom-text font-medium leading-snug line-clamp-2 group-hover:text-bloom-violet transition-colors">
                            {q.title}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${SECTION_COLORS[q.section]}`}>
                              {t(SECTION_KEYS[q.section] || 'domu.odbornici')}
                            </span>
                            <span className="text-[10px] text-bloom-sub/70">
                              {new Date(q.created_at).toLocaleDateString('cs-CZ')}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Specialists */}
            <Card className="bg-white border-border/50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-bloom-pride-pink" />
                  <h3 className="font-semibold text-bloom-text">{t('domu.nejlepeHodnoceni')}</h3>
                </div>
                {highlights.top_specialists.length === 0 ? (
                  <p className="text-sm text-bloom-sub">{t('domu.zadniHodnoceni')}</p>
                ) : (
                  <div className="space-y-3">
                    {highlights.top_specialists.map((s) => (
                      <Link key={s.id} to="/specialists" className="flex items-center gap-3 hover:bg-muted/50 rounded-lg p-1 -m-1 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-bloom-pride-pink/10 flex items-center justify-center">
                          <Stethoscope className="w-4 h-4 text-bloom-pride-pink" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-bloom-text truncate">{s.name}</p>
                          <p className="text-xs text-bloom-sub">{s.city}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-bloom-pride-pink fill-bloom-pride-pink" />
                          <span className="text-xs font-medium text-bloom-text">{s.avg_rating}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* News */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-bloom-text flex items-center">
              <span className="section-marker" style={{background: mc('news')}} />
              {nazevSekce(t, sections?.news?.title, 'nav.news', DEFAULT_SECTIONS.news?.title)}
            </h2>
            <Link to="/news" className="text-bloom-violet hover:text-bloom-violet/80 flex items-center gap-1 text-sm font-medium" data-testid="view-all-news">
              {t('obecne.vice')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {news.length === 0 ? (
            <Card className="bg-white border-border/50">
              <CardContent className="p-12 text-center">
                <Newspaper className="w-10 h-10 text-bloom-sub/30 mx-auto mb-3" />
                <p className="text-bloom-sub">{t('domu.zadneAktuality')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {news.map((item) => (
                <Link key={item.id} to={`/news/${item.id}`} data-testid={`news-${item.id}`}>
                  <Card className="bg-white border-border/50 card-hover h-full overflow-hidden">
                    <CardContent className="p-0">
                      {item.image_url && (
                        <div className="aspect-video overflow-hidden"><img src={mediaUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" /></div>
                      )}
                      <div className="p-4">
                        <h3 className="font-serif text-base font-semibold text-bloom-text mb-1 line-clamp-2">{item.title}</h3>
                        <p className="text-sm text-bloom-sub line-clamp-2 mb-2">{stripHtmlPreview(item.content, 100)}...</p>
                        <div className="flex items-center gap-1 text-xs text-bloom-sub">
                          <Calendar className="w-3 h-3" />{new Date(item.created_at).toLocaleDateString('cs-CZ')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer removed – Layout provides the unified site footer */}
    </div>
  );
};

export default HomePage;
